// Visual shell shared by all five v2 sections. Pulled out so the
// pacing of section borders / titles / collapse affordance stays
// consistent across the page.

import type { ReactNode } from 'react';

export function SectionShell({ id, title, subtitle, accent, headerExtra, children, dense }: {
  id: string;
  title: string;
  subtitle?: string;
  accent?: 'primary' | 'muted';
  headerExtra?: ReactNode;
  children: ReactNode;
  /** Tightens header padding for sections that pack a lot of inline
   *  controls (Status & Controls). */
  dense?: boolean;
}): React.ReactElement {
  const borderColor = accent === 'primary' ? 'var(--ink)' : 'var(--line)';
  return (
    <section
      id={`v2-${id}`}
      data-testid={`v2-section-${id}`}
      style={{
        background: 'var(--bg-elev)',
        border: `1px solid ${borderColor}`,
        borderLeft: `4px solid ${borderColor}`,
        borderRadius: 8,
      }}
    >
      <header style={{
        padding: dense ? '12px 18px' : '16px 22px',
        borderBottom: '1px solid var(--line)',
        display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
      }}>
        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', letterSpacing: '-0.01em' }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        {headerExtra}
      </header>
      <div style={{ padding: dense ? '12px 18px' : '18px 22px' }}>{children}</div>
    </section>
  );
}

export function EmptyState({ icon, title, body, action }: {
  icon: string;
  title: string;
  body: string;
  action?: ReactNode;
}): React.ReactElement {
  return (
    <div style={{
      padding: '24px 16px',
      textAlign: 'center',
      color: 'var(--ink-2)',
    }}>
      <div style={{ fontSize: 28, marginBottom: 8, opacity: 0.6 }}>{icon}</div>
      <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', marginBottom: 4 }}>{title}</div>
      <div style={{ fontSize: 12.5, color: 'var(--ink-3)', maxWidth: 420, margin: '0 auto', lineHeight: 1.5 }}>{body}</div>
      {action && <div style={{ marginTop: 14 }}>{action}</div>}
    </div>
  );
}
